<template>
  <div>
    <div class="table-contract-container">
      <el-form ref="form">
        <el-row>
          <el-col :span="6" class="filter-input">
            <el-input
              :placeholder="$t('keyword')"
              @input="isSearching = true"
              v-model="keyword"></el-input>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24" v-loading="isSearching">
            <div class="contract-table contract-table-head">
              <ul>
                <li style="width: 5%"></li>
                <li style="width: 15%; cursor: pointer; text-decoration: underline"
                    @click="onSortTableByKey('name')">
                  {{$t('name')}}
                  <font-awesome-icon v-if="supplementSort.sortKey === 'name'" :icon="(supplementSort.sortType === SORT_TYPE.ASC) ? 'sort-up' : 'sort-down'"/>
                </li>
                <li style="width: 15%; cursor: pointer; text-decoration: underline"
                    @click="onSortTableByKey('type')">
                  {{$t('type')}}
                  <font-awesome-icon v-if="supplementSort.sortKey === 'type'" :icon="(supplementSort.sortType === SORT_TYPE.ASC) ? 'sort-up' : 'sort-down'"/>
                </li>
                <li style="width: 10%; text-align: right">{{$t('rate')}}</li>
                <li style="width: 25%; text-align: center">{{$t('periodOfStay')}}</li>
                <li style="width: 30%">{{$t('condition')}}</li>
              </ul>
            </div>
            <draggable :list="supplementTableDataList" @change="onDraggableSupplement">
              <div v-for="(supplement, index) of supplementTableDataList" :key="`supplement-${index}`" style="cursor: move">
                <div class="contract-table" v-show="supplement.isShow">
                  <ul>
                    <li style="width: 5%; text-align: center"><el-checkbox v-model="supplementCheckList[index].isCheck" @change="onSelectedSupplementChange"></el-checkbox></li>
                    <li style="width: 15%">{{supplement.name}}</li>
                    <li style="width: 15%">{{ getSupplementTypeName(supplement.supplementTypeId) }}</li>
                    <li style="width: 10%; text-align: right">{{ bindPrice(supplement) }}</li>
                    <li style="width: 25%; text-align: center">
                      <span v-if="isAllTheTime(supplement.periodOfStayList)">{{ $t('allTheTime') }}</span>
                      <div v-for="(periodOfStay, index) of supplement.periodOfStayList" :key="`period-${index}`">
                        <span> {{bindPeriodOfStay(periodOfStay.startDate, periodOfStay.endDate)}}</span>
                      </div>
                    </li>
                    <li style="width: 30%">{{ getConditionDetail(supplement) }}</li>
                  </ul>
                  <div class="contract-data-version" v-if="isUpdatedData(supplementList, supplement)">
                    <div class="contract-data-updated">
                      <div style="padding: 20px 0 0 20px">
                        <strong>*{{$t('updatedSupplementMessage')}}</strong>
                      </div>
                      <el-radio-group style="padding-left: 40px" v-model="supplementCheckList[index].version" fill="#ff0000" @change="onSelectedSupplementChange()">
                        <div class="contract-data-version-radio">
                          <el-radio :label="DATA_VERSION.SELECTED">{{$t('selectedVersion')}} ({{bindUpdatedAt(supplement.updatedAt)}})</el-radio>
                        </div>
                        <div class="contract-data-version-radio">
                          <el-radio :label="DATA_VERSION.LATEST">{{$t('latestVersion')}} ({{bindLatestUpdatedAt(supplement.id)}})</el-radio>
                        </div>
                      </el-radio-group>
                    </div>
                  </div>
                  <div class="contract-data-version" v-if="isDeletedData(supplementList, supplement.id)">
                    <div class="contract-data-deleted">
                      <div style="padding: 20px 0 0 20px">
                        <strong style="color: #dd3333">*{{$t('deletedSupplementMessage')}}</strong>
                      </div>
                      <el-radio-group style="padding-left: 40px" v-model="supplement.id" fill="#ff0000" >
                        <el-radio :label="supplement.id" class="contract-data-version-radio">{{$t('selectedVersion')}} ({{bindUpdatedAt(supplement.updatedAt)}})</el-radio>
                      </el-radio-group>
                    </div>
                  </div>
                </div>
              </div>
            </draggable>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <remark-component
      :remark-type="REMARK_TYPE.SUPPLEMENT"
      :remark-list="defaultRemarkList"
      :contract-detail="contractDetail"
      @onRemarkChange="onRemarkChange">
    </remark-component>
  </div>
</template>

<script>
import { STATUS, SUPPLEMENT_PRICE_TYPE, REMARK_TYPE, SORT_TYPE } from '@/configs/constant'
import RemarkComponent from '@/views/Contract/components/RemarkComponent'
import i18nMessages from '@/translations/Contract/components/TabContractSupplement.i18n'
import { flattenDeep, uniq, get, debounce } from 'lodash'
import { getUnitTypeTitleKey } from '@/services/hotel-service'
import { numberFormat, bindData } from '@/helpers'
import { filterDataByKeyword } from '@/services/general-service'
import { getTableDataSource } from '@/services/contract-service'
import { applyNewRemark } from '@/domains/contract-domain'
import { dateFormat } from '@/helpers/datetime'
import {
  isUpdatedData,
  isDeletedData,
  DATA_VERSION,
  getSelectedDataListByVersion,
  getDataCheckList
} from '@/services/version-service'
import draggable from 'vuedraggable'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons'

library.add(faSortUp, faSortDown)

export default {
  name: 'TabSupplementComponent',
  components: { RemarkComponent, FontAwesomeIcon, draggable },
  i18n: { messages: i18nMessages },
  model: {
    prop: 'contractDetail',
    event: 'onSupplementDetailChange'
  },
  props: {
    defaultRemarkList: {
      type: Array,
      default: function () {
        return []
      }
    },
    supplementList: {
      type: Array,
      default: function () {
        return []
      }
    },
    supplementTypeList: {
      type: Array,
      default: function () {
        return []
      }
    },
    contractDetail: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this._filterSupplementListDataSource()
      }
    }
  },
  data () {
    return {
      SORT_TYPE,
      REMARK_TYPE,
      DATA_VERSION,
      isUpdatedData,
      isDeletedData,
      supplementTableDataList: [],
      supplementCheckList: [],
      tempRemark: '',
      newRemark: {},
      keyword: '',
      isSearching: false,
      supplementSort: {
        sortKey: 'name',
        sortType: SORT_TYPE.ASC
      }
    }
  },
  created: function () {
    this._getSupplementListDataSource()

    const supplementList = get(this.contractDetail, 'supplementList', [])
    this.supplementCheckList = getDataCheckList(this.supplementTableDataList, supplementList)
  },
  methods: {
    getSupplementTypeName: function (supplementTypeId) {
      const supplementType = this.supplementTypeList.find(item => item.id === supplementTypeId)

      return (!supplementType) ? 'N/A' : supplementType.name
    },
    bindPrice: function (row) {
      const suffix = (row.priceType === SUPPLEMENT_PRICE_TYPE.NET) ? '.-' : '%'
      const unitTypeTitleKey = getUnitTypeTitleKey(row.unitType)
      const supplementType = this.$t(`${unitTypeTitleKey}`)
      const price = numberFormat(row.price, suffix)
      return `${price} ${supplementType}`
    },
    isAllTheTime: function (periodOfStayList) {
      return (periodOfStayList === undefined || periodOfStayList.length === 0)
    },
    getSupplementIsInRoomRate: function (status) {
      switch (status) {
        case STATUS.ENABLE:
          return 'Yes'
        case STATUS.DISABLE:
          return 'No'
        default:
          return 'N/A'
      }
    },
    getConditionDetail: function (row) {
      return bindData(row, 'conditionDetail')
    },
    bindLatestUpdatedAt: function (contractSupplementId) {
      const supplement = this.supplementList.find(item => item.id === contractSupplementId)

      if (supplement !== undefined) {
        const date = new Date(supplement.updatedAt)
        return dateFormat.long(date)
      }
    },
    bindUpdatedAt: function (updatedDate) {
      const date = new Date(updatedDate)
      return dateFormat.long(date)
    },
    bindPeriodOfStay: function (startDate, endDate) {
      const fromDate = new Date(startDate)
      const toDate = new Date(endDate)

      return `${dateFormat.short(fromDate)} - ${dateFormat.short(toDate)}`
    },
    onSelectedSupplementChange () {
      const remarkList = applyNewRemark(this.contractDetail, this.newRemark)
      const supplementListResult = getSelectedDataListByVersion(
        this.supplementCheckList,
        this.supplementTableDataList,
        this.supplementList
      )
      const result = this._setSupplementOrderNumber(supplementListResult)
      const supplementList = {
        supplementList: result
      }
      const newRemarkList = {
        remarkList: remarkList
      }
      const supplementSort = {
        supplementSort: this.supplementSort
      }

      const contractDetail = Object.assign({}, this.contractDetail, supplementList, supplementSort, newRemarkList)
      this.$emit('onSupplementDetailChange', contractDetail)
    },
    onDraggableSupplement: function () {
      this.supplementSort.sortKey = 'manual'

      this._sortCheckListByIndex()
      this.onSelectedSupplementChange()
    },
    onSortTableByKey: function (key) {
      this.supplementSort.sortKey = key
      this.supplementSort.sortType = (this.supplementSort.sortType === SORT_TYPE.ASC) ? SORT_TYPE.DESC : SORT_TYPE.ASC

      let primaryKey = 'name'
      let secondaryKey = 'type'

      if (key === 'type') {
        primaryKey = 'type'
        secondaryKey = 'name'
      }

      this.supplementTableDataList.sort((prev, next) => {
        if (prev[primaryKey] > next[primaryKey]) {
          return this.supplementSort.sortType
        }
        if (prev[primaryKey] < next[primaryKey]) {
          return (this.supplementSort.sortType) * -1
        }

        if (prev[secondaryKey] > next[secondaryKey]) {
          return SORT_TYPE.ASC
        }
        if (prev[secondaryKey] < next[secondaryKey]) {
          return SORT_TYPE.DESC
        }
      })

      this._sortCheckListByIndex()
      this.onSelectedSupplementChange()
    },
    _sortCheckListByIndex: function () {
      this.supplementTableDataList.map((supplement, index) => {
        const supplementCheckItem = this.supplementCheckList.find(item => item.id === supplement.id)
        supplementCheckItem.index = index
      })

      this.supplementCheckList.sort((a, b) => (a.index > b.index) ? 1 : -1)
    },
    _setSupplementOrderNumber: function (supplementListResult) {
      const result = supplementListResult.map((item, index) => {
        const orderNumber = { orderNumber: index + 1 }
        const supplement = Object.assign({}, item, orderNumber)

        return supplement
      })

      return result
    },
    _getSupplementListDataSource: function () {
      const supplementList = get(this.contractDetail, 'supplementList', [])
      const tableResultList = getTableDataSource(this.supplementList, supplementList)
      const supplementTableDataList = tableResultList.map((supplement) => {
        const supplementType = this.supplementTypeList.find(item => item.id === supplement.supplementTypeId)
        const supplementTypeName = {
          type: (!supplementType) ? '' : supplementType.name
        }
        const result = Object.assign({}, supplement, supplementTypeName)

        return result
      })

      this.supplementSort.sortKey = get(this.contractDetail, 'supplementSort.sortKey', 'name')
      this.supplementSort.sortType = get(this.contractDetail, 'supplementSort.sortType', SORT_TYPE.ASC)
      this.supplementTableDataList = this._sortSupplementByOrderNumber(supplementTableDataList)

      this._prepareFilter()
    },
    _sortSupplementByOrderNumber: function (supplementTableDataList) {
      const supplementOrderList = supplementTableDataList.filter(item => item.orderNumber !== undefined)
      const supplementNoOrderList = supplementTableDataList.filter(item => item.orderNumber === undefined)

      supplementOrderList.sort((a, b) => (a.orderNumber > b.orderNumber) ? 1 : -1)
      supplementNoOrderList.sort((a, b) => (a.name > b.name) ? 1 : -1)

      const result = [...supplementOrderList, ...supplementNoOrderList]

      return result
    },
    _prepareFilter: function () {
      this.supplementTableDataList = this.supplementTableDataList.map((supplement) => {
        supplement.isShow = true
        return supplement
      })
    },
    _filterSupplementListDataSource: function () {
      const searchKeys = ['name']
      const filteredSupplementDataList = filterDataByKeyword(this.supplementTableDataList, this.keyword, searchKeys)
      const filterSupplementTypeList = this._filterSupplementType()

      const filteredResult = [ ...filteredSupplementDataList, ...filterSupplementTypeList ]
      const filteredList = uniq(filteredResult)

      this.supplementTableDataList = this.supplementTableDataList.map((supplement) => {
        const filteredSupplement = filteredList.find(item => item.id === supplement.id)
        supplement.isShow = (filteredSupplement !== undefined)

        return supplement
      })
    },
    _filterSupplementType: function () {
      const supplementTypeSearchKeys = ['name']
      const filteredSupplementTypeList = filterDataByKeyword(this.supplementTypeList, this.keyword, supplementTypeSearchKeys)

      const supplementListByType = filteredSupplementTypeList.map((supplementType) => {
        const supplementList = this.supplementTableDataList.filter(item => item.supplementTypeId === supplementType.id)
        return supplementList
      })

      const resultSupplement = flattenDeep(supplementListByType)
      return resultSupplement
    },
    onRemarkChange: function (newRemark) {
      this.newRemark = newRemark
      this.onSelectedSupplementChange()
    }
  }
}
</script>

<style scoped>
  .filter-input {
    left: 5%;
    position: relative;
    padding: 10px 0;
  }
</style>
